import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import Strings from "@my-dld/assets/strings";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem, LeaseDetailsBCItem } from "@my-dld/contexts/PageContext";
import PageContent from "@my-dld/components/PageContent";
import { TenantContext } from "@eres/ejari-core/contexts/TenantContext";
import { extractPropInfo } from "../component/PropertyHeader";
import { Redirect } from "react-router";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { PetInfo } from "@eres/ejari-api/Entity/co_occupants";
import { EjariApi } from "@eres/ejari-api";
import { AppContext } from "@my-dld/contexts/AppContext";
import { getEjariActiveUserId, getEjariUserType } from "@10x/sso";
import Popup from "@my-dld/components/Popup/Popup";
import { JQModal } from "@my-dld/components/Popup/Utils";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { getEjariPropertyId } from "@eres/ejari-api/Utils";
import { Lookup } from "@eres/ejari-api/Entity";
import { PetsHeader, PetsRow } from "../component/CoOccupant/Pets";
import { LocaleUtil } from "@eres/core";
import _ from "lodash";

interface PetData {
	[ id: string ]: PetInfo;
}

export default function ManagePets( props: RoutedViewProps ) {
	const tx = useContext( TenantContext );
	const property = tx.property;
	let { ContractId, ContractTypeId } = property?.CurrentContract!;
	// original co occupants
	const [ oPets, setOPets ] = useState<PetInfo[]>( [] );
	const [ oPetCount, setOPetCount ] = useState<number>( 0 );
	const [ pets, setPets ] = useState<PetInfo[]>( [] );
	const [ petLookups, setPetLookups ] = useState<Lookup[]>( [] );
	const [ data, setData ] = useState<PetData>( {} );
	useEffect( () => {
		if ( ContractId == null || ContractTypeId == null ) return;
		showLoading();
		EjariApi.getPets( ContractId, ContractTypeId )
		.then( ocl => {
			hideLoading();
			setPets( ocl );
			setOPets( ocl );
			setOPetCount( ocl.reduce( ( previousValue, currentValue ) => previousValue + currentValue.Count, 0 ) );
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}, [ ContractId, ContractTypeId ] );
	useEffect( () => {
		showLoading();
		EjariApi.getLookup( "pets" )
		.then( ocl => {
			hideLoading();
			setPetLookups( ocl );
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}, [] );
	useEffect( () => {
		const d = petLookups?.reduce( ( p: PetData, c: Lookup ) => {
			let count = pets?.find( x => x.Identity === c.Id )?.Count ?? 0;
			const cd: PetInfo = {
				...c,
				Identity: c.Id,
				Count: count,
			};
			return {
				...p,
				[ c.Id + "" ]: cd,
			};
		}, {} as PetData ) ?? {};
		setData( d );
	}, [ pets, petLookups ] );
	const updateEjari = ( ocs: PetInfo[] ) => {
		showLoading();
		const ep = Number( getEjariPropertyId( property! ) );
		return EjariApi.updatePets( {
			ActiveUserID: getEjariActiveUserId(),
			ContractId: ContractId!,
			ContractType: ContractTypeId!,
			ActiveUserType: getEjariUserType(),
			EjariPropertyId: [
				ep,
			]
		}, ocs )
		.then( r => {
			hideLoading();
			return r;
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	const onSubmit = () => {
		updateEjari( Object.values( data ) ).then( c => {
			if ( c != null ) {
				trackEventWithData( "Ejari", "UpdateCoOccupant", { contractId: ContractId } );
				JQModal( "pet_updated" ).show();
			}
		} );
	};
	const onConfirmClose = () => {
		props.history.goBack();
	};
	const onUpdate = ( update: PetInfo ) => {
		setData( d => {
			return {
				...d,
				[ update.Identity + "" ]: update,
			};
		} );
	};
	const onBatchUpdate = ( pets: PetInfo[] ) => {
		const update = pets.reduce( ( previousValue, currentValue ) => {
				return {
					...previousValue,
					[ currentValue.Identity + "" ]: currentValue,
				};
			},
			{} as PetData );
		setData( x => {
			return {
				...x,
				...update,
			};
		} );
	};
	if ( property == null )
		return <Redirect to={ "/mydld" }/>;

	let { val, lbl } = extractPropInfo( property );
	const petCount = Object.values( data ).reduce( ( p, c ) => p + c.Count, 0 );

	return (
		<PageContent
			hideQLinks={ true }
			title={ Strings.manage_pets + " - " + lbl + " : " + val }
		>
			<PageTitle title={ Strings.manage_pets } paths={ [ DashboardBCItem, LeaseDetailsBCItem ] }/>

			{ petCount === 0 && <AddPetsEmpty data={ data } onUpdate={ onBatchUpdate }/> }

			<PetsList data={ data } onSubmit={ onSubmit } onUpdate={ onUpdate } ogPetCount={ oPetCount }/>
			<AddMorePets data={ data } onUpdate={ onBatchUpdate }/>
			<Popup
				id={ "pet_updated" }
				title={ Strings.confirmation }
				showCloseButton={ true }
				clickOutsideToDismiss={ false }
				showIcon={ false }
				onDismiss={ onConfirmClose }
				footer={ <>
					<button type="button" className="btn btn_1" data-bs-dismiss="modal">
						{ Strings.close }
					</button>
				</> }
				noTitle={ false }>
				<div>
					<div>
						<div className="check-icon">
							<i className="fa fa-check"/>
						</div>
						<p className="text-center">
							{ Strings.pets_info_updated }
						</p>
					</div>
				</div>
			</Popup>
		</PageContent>
	);
}

interface AddPetForm {
	data: PetData;
	onUpdate: ( pets: PetInfo[] ) => void;
}

function AddPetsEmpty( props: AddPetForm ) {
	const { data, onUpdate } = props;
	const vals = Object.values( data );
	const [ xd, setXD ] = useState( _.cloneDeep( data ) );
	const onUpdatePet = ( pet: PetInfo ) => {
		setXD( x => {
			return {
				...x,
				[ pet.Identity + "" ]: pet,
			};
		} );
	};
	const onAdd = () => {
		onUpdate( Object.values( xd ) );
	};
	return (
		<>
			<div className="section-title justify-content-between">
				<h5>{ Strings.add_pets }</h5>
			</div>
			<div className="row">
				{ vals.map( ( value, index ) => {
						return <PetAddItem item={ value } onUpdate={ onUpdatePet } key={ index }/>;
					}
				) }
			</div>
			<button
				onClick={ onAdd }
				className="btn btn_1 btn_style1 mt-2 mb-3" type="submit">{ Strings.add_pets }</button>
		</>
	);
}

function PetAddItem( props: {
	item: PetInfo; onUpdate: ( pet: PetInfo ) => void;
	containerClass?: string;
} ) {
	const { item, onUpdate, containerClass = "col-md-3" } = props;
	const [ count, setCount ] = useState( item.Count ? ( item.Count + "" ) : "" );
	useEffect( () => {
		setCount( item.Count ? ( item.Count + "" ) : "" );
	}, [ item.Count ] );
	const onChange = ( e: ChangeEvent<HTMLInputElement> ) => {
		let v = e.target.value;
		v = v.replace( /\D/, "" );
		let n = Number( v );
		if ( n <= 0 ) v = "";
		if ( n > 999 ) v = "999";
		setCount( v );
		n = Number( v );
		onUpdate( {
			...item,
			Count: n,
		} );
	};
	useEffect( () => {
		},
		[ count ] );
	return <div className={ containerClass }>
		<div className="form-group">
			<label>{ LocaleUtil.getLocalValue( item ) }</label>
			<input
				value={ count }
				maxLength={ 10 }
				onChange={ onChange }
				type="number"
				className="form-control"/>
		</div>
	</div>;
}

interface PetsListProps {
	ogPetCount: number;
	data: PetData;
	onUpdate: ( oc: PetInfo ) => void;
	onSubmit: () => void;
}

function PetsList( props: PetsListProps ) {
	let { data, onSubmit, onUpdate, ogPetCount } = props;

	const app = useContext( AppContext );
	const pets = Object.values( data );
	const count = pets.reduce( ( p, c ) => p + c.Count, 0 );
	const hasData = count > 0;
	const cLabel = hasData ? " (" + count + ") " : "";
	const onPetDel = ( pet: PetInfo ) => {
		pet.Count = 0;
		onUpdate( pet );
	};
	const [ agreed, setAgreed ] = useState( false );
	const onCheckChange = ( event: ChangeEvent<HTMLInputElement> ) => {
		setAgreed( event.target.checked );
	};
	const showAddMore = () => {
		JQModal( "add_more_pets" ).show();
	};
	const showSubmit = hasData || count !== ogPetCount;
	const canSubmit = hasData ? agreed : ( count !== ogPetCount );
	return ( <>
			{ hasData && <>
				<div className="section-title justify-content-between">
					<h5>{ Strings.pets + cLabel } </h5>
					<button
						type="button" className="btn btn_1 btn-sm"
						onClick={ showAddMore }
					>{ Strings.add_more_pets }
					</button>
				</div>
				<table className="table table-bordered table-striped font-size14" id="occupantList">
					<PetsHeader>
						<th className="text-end">{ Strings.action }</th>
					</PetsHeader>
					<tbody>
					{ pets?.filter( x => x.Count > 0 )?.map( ( value, index ) =>
						<Row index={ index } data={ value } key={ index } onDelete={ onPetDel }/> )
					}
					</tbody>
				</table>
				<div className="mt-4 mb-3">
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="add_pet_agree"
                            checked={agreed} onChange={onCheckChange} />
                        <label className="form-check-label" htmlFor="add_pet_agree">
                            {Strings.co_oc_tc}
                        </label>
                    </div>
				</div>
			</> }
			{ showSubmit &&
				<div>
					<button
						disabled={ !canSubmit }
						onClick={ onSubmit }
						className="btn btn_1 btn_style1 mt-2" type="submit" id="btnContinue"
					>{ Strings.submit }
					</button>
				</div>
			}

		</>
	);
}

function AddMorePets( props: AddPetForm ) {
	const { data, onUpdate } = props;
	const vals = Object.values( data );
	const [ xd, setXD ] = useState( _.cloneDeep( data ) );
	const onUpdatePet = ( pet: PetInfo ) => {
		setXD( x => {
			return {
				...x,
				[ pet.Identity + "" ]: pet,
			};
		} );
	};
	const onAdd = () => {
		onUpdate( Object.values( xd ) );
		JQModal( "add_more_pets" ).hide();
	};
	return (
		<div className="modal fade" id="add_more_pets">
			<div className="modal-dialog modal-dialog-centered">
				<div className="modal-content">
					<div className="modal-header">
						<div className="modal-title">{ Strings.add_more_pets }</div>
						<button
							type="button" className="close modal-close-svg-icon" data-bs-dismiss="modal"
							aria-label="Close">
							<span className="close-svg-icon"/>
							<span className="sr-only">{ Strings.close }</span>
						</button>
					</div>
					<div className="modal-body">
						<div className="row">
							{ vals.map( ( value, index ) => {
									return <PetAddItem
										item={ value } onUpdate={ onUpdatePet }
										containerClass={ "col-md-12" }
										key={ index }/>;
								}
							) }
						</div>
					</div>
					<div className="modal-footer d-flex justify-content-center p-2">
						<button
							type="button" className="btn btn-grey"
							data-bs-dismiss="modal">{ Strings.close }</button>
						<button type="button" className="btn btn_1" onClick={ onAdd }> { Strings.add_pets }</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function Row( props: { index: number, data: PetInfo, onDelete: ( info: PetInfo ) => void } ) {
	let { data, index, onDelete } = props;
	const del = () => {
		onDelete( data );
	};
	return <PetsRow index={ index } data={ data }>
		<td data-label={ Strings.action } className="text-end">
			<button
				onClick={ del }
				className="btn btn-sm btn-link font-size14 ps-0 pe-0">{ Strings.delete }
			</button>
		</td>
	</PetsRow>;

}
